import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button, IconButton } from '../../design-system/Button';
import { Card } from '../../design-system/Card';
import { Badge } from '../../design-system/Badge';
import {
  Layers,
  Sparkles,
  ArrowRight,
  Sun,
  Moon,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Globe,
  LogIn,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const LandingPage = ({ onGetStarted, onSignIn }) => {
  const { toggleTheme, isDark } = useTheme();
  const { authError, loginWithLazplay } = useAuth();

  const methodologies = [
    {
      title: 'Software Development',
      icon: 'terminal',
      desc: 'Sprints, Kanban, Backlog, and Bug Tracking without the Jira bloat.',
      color: 'teal'
    },
    {
      title: 'Game Development',
      icon: 'sports_esports',
      desc: 'Storyboards, character sheets, level planning, and Unreal/Unity build pipelines.',
      color: 'purple'
    },
    {
      title: 'Hardware & Electronics',
      icon: 'memory',
      desc: 'Component bill of materials (BOM), PCB revisions, and validation test logs.',
      color: 'warning'
    },
    {
      title: 'Scientific Research',
      icon: 'science',
      desc: 'Hypotheses, dataset registries, literature reviews, and experiment reproducibility.',
      color: 'info'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-app)', color: 'var(--text-primary)' }}>
      {/* Landing Header */}
      <header
        style={{
          padding: '20px 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--surface-card)',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--accent-container)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              boxShadow: 'var(--shadow-raised-sm)'
            }}
          >
            <Layers size={22} strokeWidth={2.5} />
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '800' }}>
            Forma
          </span>
          <Badge size="xs" variant="teal" style={{ marginLeft: '6px' }}>
            v1.0 Production
          </Badge>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconButton
            icon={isDark ? <Sun size={18} color="#f98e65" /> : <Moon size={18} color="#23beb3" />}
            onClick={toggleTheme}
            size="sm"
            title="Toggle theme"
          />
          <Button
            variant="ghost"
            onClick={onSignIn}
            icon={<LogIn size={15} />}
          >
            Sign In
          </Button>
          <Button variant="primary" onClick={onGetStarted} icon={<ArrowRight size={16} />}>
            Start Building Free
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section
        style={{
          padding: '90px 24px 60px',
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}
      >
        <Badge variant="teal" size="md" icon={<Sparkles size={14} />}>
          Universal Modular Work Operating System
        </Badge>

        {authError && (
          <div
            style={{
              padding: '14px 20px',
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-danger)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              maxWidth: '600px'
            }}
          >
            <AlertCircle size={18} />
            <span>{authError}</span>
            <Button
              variant="primary"
              size="xs"
              onClick={loginWithLazplay}
              style={{ marginLeft: 'auto' }}
            >
              Retry Login
            </Button>
          </div>
        )}

        <h1
          style={{
            fontSize: 'clamp(36px, 6vw, 64px)',
            fontWeight: '800',
            lineHeight: '1.12',
            letterSpacing: '-0.03em',
            maxWidth: '900px'
          }}
        >
          One workspace for{' '}
          <span
            style={{
              color: 'var(--accent-teal)',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(35, 190, 179, 0.4)'
            }}
          >
            every way
          </span>{' '}
          of building.
        </h1>

        <p
          style={{
            fontSize: 'clamp(16px, 2vw, 20px)',
            color: 'var(--text-secondary)',
            maxWidth: '720px',
            lineHeight: '1.6'
          }}
        >
          Forma combines the tactile simplicity of Trello, the structured power of Jira, and the
          modularity of Notion. You choose the capabilities you need instead of being forced into
          rigid methodologies.
        </p>

        <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="primary"
            size="lg"
            onClick={onGetStarted}
            icon={<ArrowRight size={18} />}
          >
            Create Your Workspace — Free
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={onSignIn}
            icon={<LogIn size={18} />}
          >
            Sign In to Existing Account
          </Button>
        </div>

        {/* Hero Feature Badges */}
        <div
          style={{
            display: 'flex',
            gap: '24px',
            marginTop: '24px',
            color: 'var(--text-muted)',
            fontSize: '13px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle2 size={16} color="var(--accent-teal)" /> Free & Public Platform
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle2 size={16} color="var(--accent-teal)" /> Dynamic Module Activation
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle2 size={16} color="var(--accent-teal)" /> Tactical Neomorphic UI
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle2 size={16} color="var(--accent-teal)" /> Unified Lazplay Single Sign-On
          </span>
        </div>
      </section>

      {/* Methodology Showcase Cards */}
      <section
        style={{
          padding: '60px 24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
            Tailored for What You Build
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
            Select a discipline or construct your own custom setup with 11+ optional modules.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '20px'
          }}
        >
          {methodologies.map((m) => (
            <Card
              key={m.title}
              interactive
              onClick={onGetStarted}
              padding="24px"
            >
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-recessed)',
                  boxShadow: 'var(--shadow-recessed-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: '24px', color: 'var(--accent-teal)' }}
                >
                  {m.icon}
                </span>
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
                {m.title}
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                {m.desc}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* Product Principles Section */}
      <section
        style={{
          padding: '60px 24px 100px',
          maxWidth: '1100px',
          margin: '0 auto',
          borderTop: '1px solid var(--border-divider)'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}
        >
          <div
            style={{
              padding: '24px',
              backgroundColor: 'var(--surface-card)',
              boxShadow: 'var(--shadow-raised)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <Zap size={24} color="var(--accent-teal)" style={{ marginBottom: '12px' }} />
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
              Progressive Complexity
            </h4>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Start with a simple task board. Enable sprints, Gantt timeline, BOM, and automations
              only when your team needs them.
            </p>
          </div>

          <div
            style={{
              padding: '24px',
              backgroundColor: 'var(--surface-card)',
              boxShadow: 'var(--shadow-raised)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <ShieldCheck size={24} color="var(--color-success)" style={{ marginBottom: '12px' }} />
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
              Reversible Module Control
            </h4>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Disabling a module hides its views without deleting your data. Re-enable anytime to
              restore full project history.
            </p>
          </div>

          <div
            style={{
              padding: '24px',
              backgroundColor: 'var(--surface-card)',
              boxShadow: 'var(--shadow-raised)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border-subtle)'
            }}
          >
            <Globe size={24} color="var(--color-info)" style={{ marginBottom: '12px' }} />
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
              Public Sharing & Feedback
            </h4>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Share roadmaps with your community and gather public feature requests safely with
              server-enforced data boundaries.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: '32px 24px',
          borderTop: '1px solid var(--border-subtle)',
          textAlign: 'center',
          fontSize: '13px',
          color: 'var(--text-muted)',
          backgroundColor: 'var(--surface-card)'
        }}
      >
        <p>Forma — Build the way you work. Deployed on Zoho Catalyst AppSail (Node.js 22).</p>
      </footer>
    </div>
  );
};
